// useChannelProbe — verifica se um canal Xtream está "vivo" antes de
// oferecer ação ao user (ex: botão "Espiar" no toast de lembrete).
//
// Pra evitar abrir PiP e o canal estar 451/403/410/503 (geo-block,
// canal off, etc), pré-checa via HEAD na URL final assinada.
//
// Reusa pattern de pre-check do LivePlayerProvider (linhas 700-764):
// AbortController com timeout 3s, statuses bloqueados conhecidos,
// header `x-block-reason: cloudflare-tos`. Diferença: aqui é só
// "probe sim/não" — não dispara auto-switch (PiP usa o singleton
// que já faz isso na hora do play).
//
// IMPORTANTE: o probe é DESCARTÁVEL. Se a probe diz "ok" mas o canal
// fica off entre o probe e o play, o player normal trata via
// tryAutoSwitch. Probe é só pra **esconder o botão** quando já dá
// pra saber que vai falhar.

import { useEffect, useState } from 'react'

import { signStreamDeduped } from '../stream-sign'
import { rewriteStreamUrl } from '../stream-url'

const PROBE_TIMEOUT_MS = 3000

export type ProbeStatus = 'idle' | 'probing' | 'ok' | 'blocked' | 'unreachable'

interface UseChannelProbeResult {
  status: ProbeStatus
}

/** Probe leve do canal. `enabled=false` mantém em 'idle' (não dispara). */
export function useChannelProbe(channelId: string | null, enabled: boolean): UseChannelProbeResult {
  const [status, setStatus] = useState<ProbeStatus>('idle')

  useEffect(() => {
    if (!enabled || !channelId) {
      setStatus('idle')
      return
    }
    let cancelled = false
    setStatus('probing')

    void (async () => {
      try {
        // `critical: false` — probe de background (o header deste arquivo o
        // chama de DESCARTÁVEL): ninguém está esperando um frame por causa
        // dele, então não pode abrir janela crítica e represar telemetria.
        const sign = await signStreamDeduped('live', channelId, {}, { critical: false })
        if (cancelled) return
        const fmt: 'hls' | 'mpegts' | 'mp4' =
          sign.format === 'hls'
            ? 'hls'
            : sign.format === 'mp4'
              ? 'mp4'
              : 'mpegts'
        const finalUrl = rewriteStreamUrl(sign.url, fmt, sign)

        let timeoutId: ReturnType<typeof setTimeout> | null = null
        let signal: AbortSignal | undefined
        try {
          const ac = new AbortController()
          signal = ac.signal
          timeoutId = setTimeout(() => ac.abort(), PROBE_TIMEOUT_MS)
        } catch {
          signal = undefined
        }
        const head = await fetch(finalUrl, {
          method: 'HEAD',
          ...(signal ? { signal } : {}),
        })
        if (timeoutId) clearTimeout(timeoutId)
        if (cancelled) return

        const isBlocked =
          head.status === 451 ||
          head.status === 403 ||
          head.status === 410 ||
          head.status === 503 ||
          head.headers.get('x-block-reason') === 'cloudflare-tos'
        if (isBlocked) {
          setStatus('blocked')
          return
        }
        if (!head.ok && head.status !== 405) {
          // 405 (Method Not Allowed) é OK — alguns servidores não suportam
          // HEAD mas o GET vai funcionar. Só descarta em outros 4xx/5xx.
          setStatus('unreachable')
          return
        }
        setStatus('ok')
      } catch {
        if (cancelled) return
        setStatus('unreachable')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [channelId, enabled])

  return { status }
}
